import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Calculator, Gauge, Mic, ShieldCheck, Wrench } from "lucide-react";
import { setPageMeta } from "@/lib/seo";
import { EDUCATION_HUBS } from "@/data/educationHubs";
import juanPhoto from "@/assets/juan-photo.jpg";

export const HOME_TITLE = "Trailer Education, Towing & Maintenance Guides | Juan The Trailer Guy";
export const HOME_DESCRIPTION =
  "Your resource for trailer education, towing requirements, maintenance tips, and practical industry insights. Learn what matters before buying, towing, or maintaining a trailer.";

const trailerTypes = [
  { label: "Dump Trailers", href: "/dump-trailers", note: "Hoists, wall heights, 14K vs 16K." },
  { label: "Flatbed Trailers", href: "/flatbed-trailers", note: "Deck lengths, gooseneck vs bumper pull." },
  { label: "Lowboy / Equipment", href: "/lowboy-trailers", note: "Ramps, deck width, machine hauling." },
  { label: "Roll-Off Trailers", href: "/roll-off-trailers", note: "Cable pull vs hook lift, can sizes." },
  { label: "Car Haulers", href: "/car-haulers", note: "1-car to 5-car setups and tow vehicles." },
  { label: "Tilt Trailers", href: "/tilt-trailers", note: "Gravity vs power tilt explained." },
];

const featuredGuides = [
  { title: "Flatbed Trailer Weight Chart", href: "/blog/flatbed-trailer-weight-chart", tag: "GVWR" },
  { title: "14K vs 16K Dump Trailer", href: "/blog/14k-vs-16k-dump-trailer", tag: "Dump" },
  { title: "CDL Rules for Goosenecks", href: "/blog/cdl-rules-for-goosenecks", tag: "CDL" },
  { title: "Trailer Axles Explained", href: "/blog/trailer-axles-explained", tag: "Parts" },
  { title: "Trailer Maintenance Tips", href: "/blog/trailer-maintenance-tips", tag: "Maintenance" },
  { title: "How Trailer Financing Works", href: "/blog/how-trailer-financing-works", tag: "Financing" },
];

const Index = () => {
  useEffect(() => {
    setPageMeta(HOME_TITLE, HOME_DESCRIPTION, "/", { image: "/images/jtg-social.jpg", type: "website" });
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-foreground py-20 text-white md:py-28">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,hsl(var(--primary)/0.35),transparent_55%)]" aria-hidden="true" />
        <div className="container relative max-w-4xl text-center">
          <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-primary">Independent Trailer Education</p>
          <h1 className="mt-4 font-display text-4xl font-black uppercase leading-[1.05] md:text-6xl">
            Real Trailer Knowledge. Real Industry Experience.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-white/85 md:text-lg">{HOME_DESCRIPTION}</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/trailer-guides" className="inline-flex items-center justify-center gap-2 rounded-[22px] bg-primary px-7 py-4 font-display text-sm font-black uppercase tracking-wide text-primary-foreground shadow-[0_20px_50px_rgba(0,0,0,0.12)] transition hover:-translate-y-0.5">
              <BookOpen className="h-4 w-4" /> Explore Trailer Guides
            </Link>
            <Link to="/blog" className="inline-flex items-center justify-center gap-2 rounded-[22px] border-2 border-white/30 px-7 py-4 font-display text-sm font-black uppercase tracking-wide text-white transition hover:-translate-y-0.5 hover:border-white">
              Read the Latest Articles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TOPICS */}
      <section aria-labelledby="topics-heading" className="bg-background py-14 lg:py-20">
        <div className="container max-w-6xl">
          <h2 id="topics-heading" className="text-center font-display text-3xl font-black uppercase text-foreground md:text-4xl">What Do You Want to Learn?</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {EDUCATION_HUBS.map((h) => (
              <Link key={h.slug} to={h.path} className="group rounded-[22px] border border-border bg-card p-5 transition hover:border-primary">
                <h3 className="font-display text-base font-black uppercase text-foreground group-hover:text-primary">{h.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{h.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TRAILER TYPES */}
      <section aria-labelledby="types-heading" className="border-y border-border bg-muted/40 py-14 lg:py-20">
        <div className="container max-w-6xl">
          <h2 id="types-heading" className="text-center font-display text-3xl font-black uppercase text-foreground md:text-4xl">Trailer Types, Explained</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">How each design works, who uses it, and what to check before you buy or tow one.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {trailerTypes.map((t) => (
              <Link key={t.href} to={t.href} className="group flex items-start justify-between gap-3 rounded-[22px] border border-border bg-card p-5 transition hover:border-primary">
                <span>
                  <span className="block font-display text-lg font-black uppercase text-foreground group-hover:text-primary">{t.label}</span>
                  <span className="mt-1 block text-sm text-muted-foreground">{t.note}</span>
                </span>
                <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-primary" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED + TOOLS */}
      <section className="bg-background py-14 lg:py-20">
        <div className="container grid max-w-6xl gap-10 lg:grid-cols-[1.6fr_1fr]">
          <div>
            <h2 className="font-display text-3xl font-black uppercase text-foreground">Most-Read Guides</h2>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {featuredGuides.map((g) => (
                <li key={g.href}>
                  <Link to={g.href} className="block rounded-[22px] border border-border bg-card p-5 transition hover:border-primary">
                    <span className="text-[11px] font-extrabold uppercase tracking-widest text-primary">{g.tag}</span>
                    <span className="mt-1 block font-bold text-foreground">{g.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <Link to="/blog" className="mt-6 inline-flex items-center gap-2 font-bold text-primary">
              Read the Latest Articles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="space-y-4">
            <Link to="/trailer-payload-calculator" className="block rounded-[22px] bg-foreground p-6 text-white shadow-[0_20px_50px_rgba(0,0,0,0.12)]">
              <Calculator className="h-6 w-6 text-primary" />
              <p className="mt-3 font-display text-xl font-black uppercase">Trailer Payload Calculator</p>
              <p className="mt-2 text-sm text-white/80">Check real payload, cubic yards and whether your setup likely needs a CDL.</p>
            </Link>
            <Link to="/podcast" className="flex items-center gap-3 rounded-[22px] border border-border bg-card p-5 hover:border-primary">
              <Mic className="h-5 w-5 text-primary" />
              <span className="font-bold text-foreground">Listen to the Full Send podcast</span>
            </Link>
          </div>
        </div>
      </section>

      {/* WHY TRUST */}
      <section className="border-t border-border bg-muted/40 py-14 lg:py-20">
        <div className="container grid max-w-6xl items-center gap-10 md:grid-cols-[280px_1fr]">
          <img src={juanPhoto} alt="Juan Hernandez, Juan The Trailer Guy" loading="lazy" className="mx-auto aspect-square w-60 rounded-[22px] object-cover shadow-[0_20px_50px_rgba(0,0,0,0.12)]" />
          <div>
            <p className="text-xs font-extrabold uppercase tracking-widest text-primary">About Juan</p>
            <h2 className="mt-2 font-display text-3xl font-black uppercase text-foreground">Practical Knowledge From the Trailer Industry</h2>
            <p className="mt-3 text-muted-foreground">
              Juan Hernandez has spent 7+ years working with trailers: configurations, weight ratings, towing setups and the problems owners run into. This site shares that experience in plain English (y en español), with no sales pitch.
            </p>
            <ul className="mt-5 grid gap-2 text-sm text-foreground sm:grid-cols-3">
              <li className="flex items-center gap-2"><Gauge className="h-4 w-4 text-primary" /> GVWR & CDL know-how</li>
              <li className="flex items-center gap-2"><Wrench className="h-4 w-4 text-primary" /> Real maintenance advice</li>
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Independent, no sales</li>
            </ul>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 font-bold text-primary">More about Juan <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Index;
